import { Column } from '@hogwarts/ui-components-grid';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridView } from '../../../../components';
import {
  gridOptions as defaultGridOptions,
} from '../../../profilesGridUtils';

const gridOptions = {
  ...defaultGridOptions,
  rowSelection: 'single',
  sideBar: false,
  sizeColumnsToFit: true,
  defaultColDef: {
    ...defaultGridOptions.defaultColDef,
    width: 100,
    filter: true,
    editable: false,
    suppressMenu: true,
  },
  suppressRowClickSelection: true,
  suppressCellSelection: true,
};

interface DocumentManifestRow {
  id?: string;
  locationName: string;
  userId: string;
  documentId?: string;
  filename: string;
  note?: string;
}

interface SummaryRow {
  locationName: string;
  fileCount: number;
  note: string;
}

const buildSummaryRows = (rows: DocumentManifestRow[]): SummaryRow[] => {
  const byLocation = new Map<string, SummaryRow>();

  for (const row of rows) {
    const existing = byLocation.get(row.locationName) || {
      locationName: row.locationName,
      fileCount: 0,
      note: '',
    };

    if (row.note) {
      existing.note = row.note;
    } else {
      existing.fileCount += 1;
    }

    byLocation.set(row.locationName, existing);
  }

  return Array.from(byLocation.values()).sort((a, b) =>
    a.locationName.localeCompare(b.locationName)
  );
};

const DocumentManifestGridView = ({
  rows,
  view,
}: {
  rows: DocumentManifestRow[];
  view: 'detail' | 'summary';
}) => {
  const { t } = useTranslation();

  const detailColumns: Column[] = useMemo(
    () => [
      {
        key: 'locationName',
        label: t('Location'),
        width: 280,
      },
      {
        key: 'userId',
        label: t('User ID'),
        width: 160,
      },
      {
        key: 'documentId',
        label: t('Document ID'),
        width: 220,
      },
      {
        key: 'filename',
        label: t('Filename'),
        width: 360,
      },
      {
        key: 'note',
        label: t('Note'),
        width: 280,
      },
    ],
    [t]
  );

  const summaryColumns: Column[] = useMemo(
    () => [
      {
        key: 'locationName',
        label: t('Location'),
        width: 400,
      },
      {
        key: 'fileCount',
        label: t('File Count'),
        width: 120,
      },
      {
        key: 'note',
        label: t('Note'),
        width: 280,
      },
    ],
    [t]
  );

  const summaryRows = useMemo(() => buildSummaryRows(rows || []), [rows]);

  if (view === 'summary') {
    return (
      <div className="h-100">
        <GridView
          {...gridOptions}
          getRowNodeId={(data: SummaryRow) => data.locationName}
          groups={[]}
          columns={summaryColumns}
          rows={summaryRows}
          showSideBar={false}
        />
      </div>
    );
  }

  return (
    <div className="h-100">
      <GridView
        {...gridOptions}
        getRowNodeId={(data: DocumentManifestRow) =>
          data.id ||
          data.documentId ||
          `skipped:${data.locationName}:${data.note || ''}`
        }
        groups={[]}
        columns={detailColumns}
        rows={rows}
        showSideBar={false}
      />
    </div>
  );
};

export default DocumentManifestGridView;
